import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Badge, Button, Checkbox, Container, Group, SegmentedControl, Stack, Text, Title } from "@mantine/core"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { keepPreviousData, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyMutation, useCompanyQuery } from "../../hooks/useCompanyQuery"
import { useJargonSuggestedEvents } from "../../hooks/useSSE"
import {
  acceptJargonSuggestion,
  addVocabTerms,
  bulkDecideJargon,
  dismissJargonSuggestion,
  fetchJargonSuggestionsPage,
  fetchVocab,
  fetchVocabStats,
  removeVocabTerm,
} from "../../lib/api"
import type { IJargonSuggestionPage, TBulkJargonAction, TJargonSort, TJargonStatus } from "../../lib/api/jargon"
import { notifyError } from "../../lib/notify"
import { queryKeys } from "../../lib/query-keys"
import { groupJargonByMeeting, partitionSeenOnce } from "./-jargon/group"
import { JargonDictionary } from "./-jargon/JargonDictionary"
import { JargonManagerList } from "./-jargon/JargonManagerList"
import classes from "./jargon.module.css"

export const Route = createFileRoute("/_auth/jargon")({
  component: JargonPage,
})

/** Review-queue page size; "Show more" grows the requested limit by this step. */
const PAGE_SIZE = 50

/** How long to let a decided row collapse out before it's actually removed. Matches
 *  `--motion-duration-base` (the row collapse animation); skipped under reduced motion. */
const EXIT_MS = 220

const EMPTY_PAGE: IJargonSuggestionPage = { suggestions: [], total: 0 }

/** Does the viewer prefer reduced motion? (No exit delay for them.) */
function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

/** Narrow the SegmentedControl's `string` value back to a status; defaults to pending. */
function isJargonStatus(value: string): value is TJargonStatus {
  return value === "pending" || value === "accepted" || value === "dismissed"
}

/** A single-row decision — accept (optionally with a corrected spelling) or dismiss. */
type TDecision = { action: "accept" | "dismiss"; sid: string; term?: string }
/** A bulk decision across a selection of suggestions. */
type TBulkDecision = { ids: string[]; action: TBulkJargonAction }
/** Rollback snapshot for an optimistic decision. */
type TDecideContext = { previous: IJargonSuggestionPage | undefined }

/**
 * Drop every row sharing a selected row's term from the cached page (mirrors the
 * backend's dedupe-by-term), decrementing the total by however many vanished.
 */
function removeByTerms(page: IJargonSuggestionPage, ids: string[]): IJargonSuggestionPage {
  const idSet = new Set(ids)
  const terms = new Set(page.suggestions.filter((s) => idSet.has(s.id)).map((s) => s.term.toLowerCase()))
  const suggestions = page.suggestions.filter((s) => !terms.has(s.term.toLowerCase()))
  const removed = page.suggestions.length - suggestions.length
  return { suggestions, total: Math.max(0, page.total - removed) }
}

/** Narrow the SegmentedControl's `string` value back to a sort; the page opens on frequency. */
function isJargonSort(value: string): value is TJargonSort {
  return value === "newest" || value === "frequency"
}

function JargonPage(): React.JSX.Element {
  const [status, setStatus] = useState<TJargonStatus>("pending")
  const [sort, setSort] = useState<TJargonSort>("frequency")
  /** Captured once so the 14-day fold doesn't shift on every render. */
  const [now] = useState(() => Date.now())
  const [limit, setLimit] = useState(PAGE_SIZE)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [exiting, setExiting] = useState<Set<string>>(new Set())

  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const queryClient = useQueryClient()
  const { i18n } = useLingui()

  // Exact key of the page below — every optimistic edit targets it directly.
  const suggestionsKey = queryKeys.jargonSuggestions(orgId, status, limit, sort)

  const {
    data: page = EMPTY_PAGE,
    isLoading: suggestionsLoading,
    isFetching: suggestionsFetching,
  } = useCompanyQuery({
    key: (org) => queryKeys.jargonSuggestions(org, status, limit, sort),
    queryFn: (company) => fetchJargonSuggestionsPage(company, { status, limit, sort }),
    placeholderData: keepPreviousData,
  })

  const partitioned = useMemo(
    () =>
      status === "pending" ? partitionSeenOnce(page.suggestions, now) : { review: page.suggestions, seenOnce: [] },
    [page.suggestions, status, now],
  )
  const groups = useMemo(() => groupJargonByMeeting(partitioned.review), [partitioned.review])
  const loadedIds = useMemo(() => partitioned.review.map((s) => s.id), [partitioned.review])
  const allVisibleSelected = loadedIds.length > 0 && loadedIds.every((id) => selected.has(id))
  const someVisibleSelected = loadedIds.some((id) => selected.has(id))

  const { data: vocabData, isLoading: vocabLoading } = useCompanyQuery({
    key: queryKeys.vocab,
    queryFn: (company) => fetchVocab(company),
  })
  const vocabTerms = vocabData?.terms ?? []

  const { data: vocabStats } = useCompanyQuery({
    key: queryKeys.vocabStats,
    queryFn: fetchVocabStats,
  })

  // A meeting finished processing and produced new candidates — refill the queue
  // live so the reviewer never has to reload to see them.
  useJargonSuggestedEvents(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.jargonSuggestions(orgId) })
  })

  const handleStatusChange = (next: string): void => {
    if (!isJargonStatus(next)) return
    setStatus(next)
    setLimit(PAGE_SIZE)
    setSelected(new Set())
  }

  const handleSortChange = (next: string): void => {
    if (!isJargonSort(next)) return
    setSort(next)
    setLimit(PAGE_SIZE)
  }

  const toggleSelect = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleGroup = (ids: string[], select: boolean): void => {
    setSelected((prev) => {
      const next = new Set(prev)
      for (const id of ids) {
        if (select) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }

  const toggleAllVisible = (): void => {
    setSelected((prev) => {
      if (loadedIds.every((id) => prev.has(id))) return new Set()
      return new Set(loadedIds)
    })
  }

  // Play the collapse-on-decide animation for `ids`, then run the actual
  // mutation (which removes them from cache). Under reduced motion the delay is
  // skipped so the rows go immediately.
  const animateOutThenRun = (ids: string[], run: () => void): void => {
    if (ids.length === 0) return
    if (prefersReducedMotion()) {
      run()
      return
    }
    setExiting((prev) => new Set([...prev, ...ids]))
    window.setTimeout(() => {
      run()
      setExiting((prev) => {
        const next = new Set(prev)
        for (const id of ids) next.delete(id)
        return next
      })
    }, EXIT_MS)
  }

  // Single-row accept / dismiss (review). Keeps the edit-before-accept path the
  // bulk endpoint can't carry; optimistically clears the term's siblings.
  const decideMutation = useCompanyMutation<{ term: string; status: string }, TDecision, Error, TDecideContext>({
    mutationFn: (company, { action, sid, term }) =>
      action === "accept" ? acceptJargonSuggestion(company, sid, term) : dismissJargonSuggestion(company, sid),
    onMutate: async ({ sid }) => {
      await queryClient.cancelQueries({ queryKey: suggestionsKey })
      const previous = queryClient.getQueryData<IJargonSuggestionPage>(suggestionsKey)
      queryClient.setQueryData<IJargonSuggestionPage>(suggestionsKey, (old) => (old ? removeByTerms(old, [sid]) : old))
      setSelected((prev) => {
        const next = new Set(prev)
        next.delete(sid)
        return next
      })
      return { previous }
    },
    onError: (err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(suggestionsKey, context.previous)
      notifyError(err)
    },
    onSuccess: (res, { action }) => {
      notifications.show({
        message:
          action === "accept"
            ? i18n._(msg`Added "${res.term}" to your dictionary`)
            : i18n._(msg`Dismissed "${res.term}"`),
        color: action === "accept" ? "success" : "gray",
      })
    },
    onSettled: (_res, _err, { action }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jargonSuggestions(orgId) })
      if (action === "accept") queryClient.invalidateQueries({ queryKey: queryKeys.vocab(orgId) })
    },
  })

  // Bulk accept / dismiss / restore across a selection (or a single history row).
  const bulkMutation = useCompanyMutation<
    { action: TBulkJargonAction; terms: string[] },
    TBulkDecision,
    Error,
    TDecideContext
  >({
    mutationFn: (company, { ids, action }) => bulkDecideJargon(company, ids, action),
    onMutate: async ({ ids }) => {
      await queryClient.cancelQueries({ queryKey: suggestionsKey })
      const previous = queryClient.getQueryData<IJargonSuggestionPage>(suggestionsKey)
      queryClient.setQueryData<IJargonSuggestionPage>(suggestionsKey, (old) => (old ? removeByTerms(old, ids) : old))
      setSelected(new Set())
      return { previous }
    },
    onError: (err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(suggestionsKey, context.previous)
      notifyError(err)
    },
    onSuccess: ({ action, terms }) => {
      const count = terms.length
      notifications.show({
        message:
          action === "accept"
            ? i18n._(msg`Added ${count} term(s) to your dictionary`)
            : action === "dismiss"
              ? i18n._(msg`Dismissed ${count} term(s)`)
              : i18n._(msg`Restored ${count} term(s) to review`),
        color: action === "dismiss" ? "gray" : "success",
      })
    },
    onSettled: (_res, _err, { action }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jargonSuggestions(orgId) })
      if (action === "accept") queryClient.invalidateQueries({ queryKey: queryKeys.vocab(orgId) })
    },
  })

  const addVocabMutation = useCompanyMutation({
    mutationFn: (company, terms: string[]) => addVocabTerms(company, terms),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.vocab(orgId) }),
    onError: (err) => notifyError(err),
  })

  const removeVocabMutation = useCompanyMutation({
    mutationFn: (company, term: string) => removeVocabTerm(company, term),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.vocab(orgId) }),
    onError: (err) => notifyError(err),
  })

  const handleRemoveVocab = (term: string): void => {
    modals.openConfirmModal({
      title: i18n._(msg`Remove term`),
      children: (
        <Text size="sm">
          <Trans>
            Remove "{term}" from your dictionary? It won't be sent to your transcription engine as a hint anymore.
          </Trans>
        </Text>
      ),
      labels: { confirm: i18n._(msg`Remove`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: () => removeVocabMutation.mutate(term),
    })
  }

  const runBulk = (action: TBulkJargonAction): void => {
    const ids = Array.from(selected)
    if (ids.length > 0) animateOutThenRun(ids, () => bulkMutation.mutate({ ids, action }))
  }

  const isReview = status === "pending"
  const selectedCount = selected.size

  return (
    <Container size="md">
      <Stack gap="xl">
        <div>
          <Title order={2}>
            <Trans>Jargon</Trans>
          </Title>
          <Text c="dimmed" size="sm" mt={4}>
            <Trans>
              Domain words, names, and acronyms sent to the transcription engine so it gets them right. Review
              suggestions from your meetings, then add the keepers to your dictionary.
            </Trans>
          </Text>
        </div>

        {/* --- Suggestions queue --- */}
        <Stack gap="sm">
          <Group gap="xs" justify="space-between" wrap="wrap">
            <Group gap="xs">
              <Title order={4}>
                <Trans>Suggestions</Trans>
              </Title>
              {page.total > 0 && (
                <Badge variant="light" color="brand">
                  <span key={page.total} className="lex-count-tick">
                    {page.total}
                  </span>
                </Badge>
              )}
            </Group>
            <Group gap="xs">
              <SegmentedControl
                size="xs"
                value={sort}
                onChange={handleSortChange}
                aria-label={i18n._(msg`Sort suggestions`)}
                data={[
                  { label: i18n._(msg`Newest`), value: "newest" },
                  { label: i18n._(msg`Most frequent`), value: "frequency" },
                ]}
              />
              <SegmentedControl
                size="xs"
                value={status}
                onChange={handleStatusChange}
                data={[
                  { label: i18n._(msg`Pending`), value: "pending" },
                  { label: i18n._(msg`Accepted`), value: "accepted" },
                  { label: i18n._(msg`Dismissed`), value: "dismissed" },
                ]}
              />
            </Group>
          </Group>

          {status === "accepted" && (
            <Text size="xs" c="dimmed">
              <Trans>
                Restoring sends a term back to review — it stays in your dictionary until you remove it below.
              </Trans>
            </Text>
          )}

          {/* --- Selection / bulk-action bar --- */}
          {groups.length > 0 && (
            <div className={classes.bulkBar} data-active={selectedCount > 0 || undefined}>
              <Checkbox
                checked={allVisibleSelected}
                indeterminate={!allVisibleSelected && someVisibleSelected}
                onChange={toggleAllVisible}
                label={selectedCount > 0 ? <Trans>{selectedCount} selected</Trans> : <Trans>Select all</Trans>}
              />
              {selectedCount > 0 && (
                <div className={classes.bulkActions}>
                  {isReview ? (
                    <>
                      <Button
                        size="xs"
                        color="success"
                        variant="filled"
                        loading={bulkMutation.isPending}
                        onClick={() => runBulk("accept")}
                      >
                        <Trans>Add to dictionary</Trans>
                      </Button>
                      <Button
                        size="xs"
                        color="danger"
                        variant="light"
                        loading={bulkMutation.isPending}
                        onClick={() => runBulk("dismiss")}
                      >
                        <Trans>Dismiss</Trans>
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="xs"
                      variant="filled"
                      loading={bulkMutation.isPending}
                      onClick={() => runBulk("restore")}
                    >
                      <Trans>Restore to pending</Trans>
                    </Button>
                  )}
                  <Button size="xs" variant="subtle" color="gray" onClick={() => setSelected(new Set())}>
                    <Trans>Clear</Trans>
                  </Button>
                </div>
              )}
            </div>
          )}

          <JargonManagerList
            mode={isReview ? "review" : "history"}
            groups={groups}
            seenOnce={isReview ? partitioned.seenOnce : undefined}
            isLoading={suggestionsLoading}
            selectedIds={selected}
            exitingIds={exiting}
            onToggleSelect={toggleSelect}
            onToggleGroup={toggleGroup}
            onAccept={(sid, term) =>
              animateOutThenRun([sid], () => decideMutation.mutate({ action: "accept", sid, term }))
            }
            onDismiss={(sid) => animateOutThenRun([sid], () => decideMutation.mutate({ action: "dismiss", sid }))}
            onRestore={(sid) => animateOutThenRun([sid], () => bulkMutation.mutate({ ids: [sid], action: "restore" }))}
            onBulkGroup={(ids, action) => animateOutThenRun(ids, () => bulkMutation.mutate({ ids, action }))}
            total={page.total}
            loadedCount={page.suggestions.length}
            onLoadMore={() => setLimit((l) => l + PAGE_SIZE)}
            isLoadingMore={suggestionsFetching}
          />
        </Stack>

        {/* --- Accepted dictionary --- */}
        <Stack gap="sm">
          <Group gap="xs">
            <Title order={4}>
              <Trans>Dictionary</Trans>
            </Title>
            {vocabTerms.length > 0 && (
              <Badge variant="light" color="info">
                {vocabTerms.length}
              </Badge>
            )}
          </Group>
          <JargonDictionary
            terms={vocabTerms}
            stats={vocabStats?.terms}
            meetingsCounted={vocabStats?.meetingsCounted}
            isLoading={vocabLoading}
            isAdding={addVocabMutation.isPending}
            onAdd={(terms) => addVocabMutation.mutate(terms)}
            onRemove={handleRemoveVocab}
          />
        </Stack>
      </Stack>
    </Container>
  )
}
